/**
 * 模块: YearOverviewPanel
 * 职责: 年度周期概览面板组件
 * 限制: <280行
 */

import React from 'react';
import { Button, Card, Popconfirm, message } from 'antd';
import {
  UploadOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { WeekOverview } from './yearOverviewUtils';

interface YearOverviewPanelProps {
  selectedYear: number;
  setSelectedYear: (year: number) => void;
  uploadedCount: number;
  availableYears: number[];
  weekOverview: WeekOverview[];
  uploading: boolean;
  uploadProgress: Record<string, string>;
  triggerFileSelect: () => void;
  onDelete: (periodId: number) => Promise<void>;
}

export const YearOverviewPanel: React.FC<YearOverviewPanelProps> = ({
  selectedYear,
  setSelectedYear,
  uploadedCount,
  availableYears,
  weekOverview,
  uploading,
  uploadProgress,
  triggerFileSelect,
  onDelete,
}) => {
  // 处理周期删除
  const handleDelete = async (periodId: number) => {
    try {
      await onDelete(periodId);
      message.success('删除成功');
    } catch (error) {
      message.error('删除失败');
    }
  };

  const currentYear = new Date().getFullYear();
  const minYear = Math.min(...availableYears, currentYear - 1);
  const maxYear = Math.max(...availableYears, currentYear);

  return (
    <div style={{ marginBottom: 24, padding: '20px', background: '#fff', borderRadius: 8 }}>
      {/* 头部 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <span style={{ fontSize: 16, fontWeight: 500 }}>
            📊 {selectedYear}年周期数据概览
          </span>
          <span style={{ fontSize: 12, color: '#999', marginLeft: 12 }}>
            (共 52 周，已上传 {uploadedCount} 周)
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* 图例 */}
          <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }}></span>
              已上传
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f0f0f0', display: 'inline-block' }}></span>
              未上传
            </span>
          </div>
          {/* 年份选择器 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button
              size="small"
              disabled={selectedYear <= minYear}
              onClick={() => setSelectedYear(selectedYear - 1)}
            >
              ◀
            </Button>
            <span style={{ minWidth: 50, textAlign: 'center' }}>{selectedYear}</span>
            <Button
              size="small"
              disabled={selectedYear >= maxYear}
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              ▶
            </Button>
          </div>
        </div>
      </div>

      {/* 周期网格 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', 
        gap: 8 
      }}>
        {weekOverview.map(week => (
          <Card
            key={week.weekLabel}
            size="small"
            style={{
              borderColor: week.uploaded ? '#52c41a' : '#d9d9d9',
              background: week.uploaded ? '#f6ffed' : '#fafafa',
              cursor: 'pointer',
            }}
            onClick={() => !week.uploaded && triggerFileSelect()}
          >
            <div style={{ textAlign: 'center', fontSize: 12 }}>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                {week.weekDisplayLabel}
              </div>
              <div>
                {week.uploaded ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    {week.period && (
                      <Popconfirm
                        title="确定要删除这个周期的数据吗？"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleDelete(week.period!.id);
                        }}
                        okText="确定删除"
                        cancelText="取消"
                        okButtonProps={{ danger: true }}
                      >
                        <DeleteOutlined
                          style={{ color: '#ff4d4f', fontSize: 12 }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Popconfirm>
                    )}
                  </div>
                ) : (
                  <PlusOutlined style={{ color: '#d9d9d9' }} />
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* 批量上传按钮 */}
      <div style={{ marginTop: 16 }}>
        <Button
          type="dashed"
          icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
          style={{ width: '100%', height: 50 }}
          loading={uploading}
          onClick={triggerFileSelect}
        >
          {uploading ? '上传中...' : '批量上传周期'}
        </Button>
        
        {/* 上传进度显示 */}
        {Object.keys(uploadProgress).length > 0 && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
            {Object.entries(uploadProgress).map(([file, status]) => (
              <div key={file} style={{ marginBottom: 4 }}>
                {file}: {status}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

